import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import Service, { service } from '@ember/service';
import { isTesting } from '@embroider/macros';

import { tracked } from '@glimmer/tracking';

import { task } from 'ember-concurrency';

import merge from 'lodash/merge';

import type { SingleCardDocument } from '@cardstack/runtime-common';

import config from '@cardstack/host/config/environment';
import type CardService from '@cardstack/host/services/card-service';
import type ResetService from '@cardstack/host/services/reset';
import {
  type MonacoLanguageConfig,
  extendDefinition,
  extendConfig,
  languageConfigs,
} from '@cardstack/host/utils/editor/editor-language';
import { createMonacoWaiterManager } from '@cardstack/host/utils/editor/monaco-test-waiter';

import type * as _MonacoSDK from 'monaco-editor';

export type MonacoSDK = typeof _MonacoSDK;
export type IStandaloneCodeEditor = _MonacoSDK.editor.IStandaloneCodeEditor;

const { serverEchoDebounceMs } = config;

export default class MonacoService extends Service {
  #ready: Promise<MonacoSDK>;
  #monacoSDK: MonacoSDK | undefined;
  @tracked editor: _MonacoSDK.editor.ICodeEditor | null = null;
  @tracked hasFocus = false;
  @service declare cardService: CardService;
  @service declare reset: ResetService;
  // this is in the service so that we can manipulate it in our tests
  serverEchoDebounceMs = serverEchoDebounceMs;

  private waiterManager = createMonacoWaiterManager();

  constructor(owner: Owner) {
    super(owner);
    this.reset.register(this);
    this.#ready = this.loadMonacoSDK.perform();
  }

  resetState() {
    this.editor?.dispose?.();
    this.editor = null;
    this.hasFocus = false;
    this.trackedSelection = undefined;
    for (let model of this.#monacoSDK?.editor.getModels() ?? []) {
      if (!model.isDisposed() && !model.isAttachedToEditor()) {
        model.dispose();
      }
    }
  }

  private loadMonacoSDK = task(async () => {
    // @ts-expect-error: dynamic import without types
    await import('@cardstack/requirejs-monaco-ember-polyfill');
    const monaco = await import('monaco-editor');
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions(
      this.defaultCompilerOptions(monaco),
    );
    let promises = languageConfigs.map((lang) =>
      this.extendMonacoLanguage(lang, monaco),
    );
    monaco.editor.setTheme('vs-dark');
    monaco.editor.onDidCreateEditor((editor: _MonacoSDK.editor.ICodeEditor) => {
      let isMainEditor = ((editor as any)._domElement as HTMLElement)
        .getAttributeNames()
        .includes('data-monaco-container-operator-mode');

      if (!isMainEditor) {
        // Other editors (code blocks) are read only, so we don't need to track focus
        return;
      }

      // Track editor initialization with shared waiter manager
      const initOperation = `editor-init-${editor.getId()}`;

      this.editor = editor;
      this.editor.onDidFocusEditorText(() => {
        this.hasFocus = true;
      });
      this.editor.onDidBlurEditorText(() => {
        this.hasFocus = false;
      });
      this.editor.onDidChangeCursorSelection(() => {
        debounce(this, this.updateSelection, isTesting() ? 10 : 200);
      });
      this.editor.onDidDispose(() => {
        if (this.editor === editor) {
          this.editor = null;
          this.hasFocus = false;
          this.trackedSelection = undefined;
        }
      });

      // Use shared waiter manager to track editor initialization
      if (this.waiterManager) {
        this.waiterManager.trackEditorInit(this.editor, initOperation);
      }
    });
    await Promise.all(promises);
    this.#monacoSDK = monaco;
    return monaco;
  });

  // === context ===
  // A context is needed to pass a loaded sdk into components and modifiers
  // The monaco sdk is dynamically loaded when visiting /code route
  async getMonacoContext(): Promise<MonacoSDK> {
    return await this.#ready;
  }

  // File serialization is a special type of card serialization that the host would
  // otherwise not encounter, but it does here since it's using the accept header
  // application/vnd.card+source to load the file that we see in monaco. This is
  // the only place that we use this accept header for loading card instances--everywhere
  // else we use application/vnd.card+json. Because of this the resulting JSON has
  // different semantics than the host would normally encounter--for instance, this
  // file serialization format is always missing an ID (because the ID is the filename).
  // Whereas for card instances obtained via application/vnd.card+json, a missing ID
  // means that the card is not saved.
  //
  // In order to prevent confusion around which type of serialization you are dealing
  // with, we convert the file serialization back to the form the host is accustomed
  // to (application/vnd.card+json) as soon as possible so that the semantics around
  // file serialization don't leak outside of where they are immediately used.
  reverseFileSerialization(
    fileSerializationJSON: SingleCardDocument,
    id: string,
    realmURL: string,
  ): SingleCardDocument {
    return merge({}, fileSerializationJSON, {
      data: {
        id,
        type: 'card',
        meta: {
          realmURL,
        },
      },
    });
  }

  private async extendMonacoLanguage(
    { baseId, langInfo, rules }: MonacoLanguageConfig,
    sdk: MonacoSDK,
  ) {
    const baseLanguage = sdk.languages
      .getLanguages()
      .find((lang) => lang.id === baseId);

    if (!baseLanguage) {
      throw new Error(`missing language ${baseId}`);
    }

    // @ts-expect-error: types don't declare loader
    let { conf, language } = await baseLanguage.loader();

    let extendedConfig = extendConfig(conf);
    let extendedDef = extendDefinition(language, rules);
    let { id } = langInfo;

    sdk.languages.register(langInfo);
    sdk.languages.setMonarchTokensProvider(id, extendedDef);
    sdk.languages.setLanguageConfiguration(id, extendedConfig);
  }

  private defaultCompilerOptions(
    sdk: MonacoSDK,
  ): _MonacoSDK.languages.typescript.CompilerOptions {
    return {
      target: sdk.languages.typescript.ScriptTarget.ES2020,
      module: sdk.languages.typescript.ModuleKind.ES2015,
      moduleResolution: sdk.languages.typescript.ModuleResolutionKind.NodeJs,
      allowJs: true,
      allowSyntheticDefaultImports: true,
      noImplicitAny: true,
      noImplicitThis: true,
      alwaysStrict: true,
      strictNullChecks: true,
      strictPropertyInitialization: true,
      noFallthroughCasesInSwitch: true,
      noUnusedLocals: true,
      noUnusedParameters: true,
      noImplicitReturns: true,
      noEmitOnError: true,
      noEmit: true,
      inlineSourceMap: true,
      inlineSources: true,
      experimentalDecorators: true,
      allowNonTsExtensions: true,
    };
  }

  getMonacoContent() {
    let model = this.editor?.getModel();
    if (!model) {
      return null;
    }
    return model.getValue();
  }

  getLineCursorOn(): string | null {
    let model = this.editor?.getModel();
    if (!model || !this.editor) {
      return null;
    }

    let currentPosition = this.editor.getPosition();
    return currentPosition
      ? model.getLineContent(currentPosition.lineNumber)
      : null;
  }

  getCursorPosition() {
    return this.editor?.getPosition();
  }

  updateCursorPosition(cursorPosition: _MonacoSDK.Position) {
    if (!this.editor) {
      return;
    }
    this.editor.focus();
    this.editor.setPosition(cursorPosition);
    this.editor.revealLineNearTop(cursorPosition.lineNumber);
  }

  getContentHeight() {
    return this.editor?.getContentHeight();
  }

  getSelection() {
    return this.editor?.getSelection();
  }

  @tracked trackedSelection = this.getSelection();
  updateSelection() {
    this.trackedSelection = this.getSelection();
  }
}

declare module '@ember/service' {
  interface Registry {
    'monaco-service': MonacoService;
  }
}
